import { useMemo, useState } from 'react';
import { Modal } from '@mantine/core';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/Button';
import { Card, CardHead } from '@/components/Card';
import { DataGrid, type ColumnDef } from '@/components/DataGrid';
import { EmptyState } from '@/components/EmptyState';
import { Icon } from '@/components/Icon';
import { Status } from '@/components/Status';
import {
  useCreatePartner,
  useCreatePartnerKey,
  usePartner,
  usePartnerKeys,
  usePartners,
  useRevokePartnerKey,
  useUpdateKeyOrigin,
} from '@/hooks/usePartners';
import type { ApiKey, KeyRecruitmentSource, IssuedKey, Partner } from '@/api/partners';
import { RECRUITMENT_ORIGINS } from '@/api/imports';
import { fmt, formatDateTime } from '@/lib/format';

/**
 * Partners module. Two-pane UI:
 *
 *  Left  → list of partners with a search box and a "+ Nouveau" button.
 *  Right → detail of the selected partner, including its API keys.
 *
 * Two modals piggyback on this page:
 *  • CreatePartnerModal — minimal form (name only is required)
 *  • CreateKeyModal      — generates a key and shows the plaintext ONCE,
 *                          with a copy button, mirroring the security
 *                          contract documented on the backend.
 */
export function PartnersPage() {
  const { t } = useTranslation();
  const [search, setSearch] = useState('');
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [createOpen, setCreateOpen] = useState(false);

  const list = usePartners({ search: search || undefined, limit: 100 });
  const partners = list.data?.items ?? [];

  // Auto-select the first partner once the list lands.
  if (selectedId == null && partners.length > 0) {
    setSelectedId(partners[0].id);
  }

  return (
    <div className="fade-in">
      <div className="page-head">
        <div>
          <div className="pt">{t('pages.partners.title')}</div>
          <div className="ps">{t('pages.partners.subtitle')}</div>
        </div>
        <div className="ph-right">
          <Button variant="primary" icon="plus" onClick={() => setCreateOpen(true)}>
            Nouveau partenaire
          </Button>
        </div>
      </div>

      <div className="grid partners-split" style={{ gridTemplateColumns: '320px minmax(0, 1fr)', gap: 22 }}>
        <Card style={{ height: 'fit-content' }}>
          <div style={{ padding: '14px 16px', borderBottom: '1px solid var(--line)' }}>
            <label className="input">
              <Icon name="search" />
              <input
                placeholder="Rechercher un partenaire"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </label>
          </div>
          <div style={{ maxHeight: '70vh', overflowY: 'auto' }}>
            {list.isLoading && (
              <div className="muted" style={{ padding: 24, textAlign: 'center' }}>
                Chargement…
              </div>
            )}
            {!list.isLoading && partners.length === 0 && (
              <EmptyState
                icon="plug"
                title="Aucun partenaire"
                subtitle="Créez votre premier partenaire pour commencer à émettre des clés API."
              />
            )}
            {partners.map((p) => (
              <PartnerListItem
                key={p.id}
                partner={p}
                active={p.id === selectedId}
                onClick={() => setSelectedId(p.id)}
              />
            ))}
          </div>
        </Card>

        <div style={{ minWidth: 0 }}>
          {selectedId != null ? (
            <PartnerDetail partnerId={selectedId} />
          ) : (
            <Card pad>
              <EmptyState icon="plug" title="Sélectionnez un partenaire" />
            </Card>
          )}
        </div>
      </div>

      <CreatePartnerModal
        opened={createOpen}
        onClose={() => setCreateOpen(false)}
        onCreated={(p) => {
          setSelectedId(p.id);
          setCreateOpen(false);
        }}
      />
    </div>
  );
}

function PartnerListItem({
  partner,
  active,
  onClick,
}: {
  partner: Partner;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      onClick={onClick}
      style={{
        width: '100%',
        textAlign: 'left',
        cursor: 'pointer',
        padding: '14px 16px',
        background: active ? 'var(--accent-tint)' : 'transparent',
        borderBottom: '1px solid var(--line)',
        border: 'none',
        borderLeft: active ? '3px solid var(--accent)' : '3px solid transparent',
        display: 'block',
      }}
    >
      <div style={{ fontWeight: 800, fontSize: 14, color: active ? 'var(--accent-ink)' : 'var(--ink)' }}>
        {partner.name}
      </div>
      <div className="muted" style={{ fontSize: 12, fontWeight: 600, marginTop: 4 }}>
        {partner.contactName ?? '—'} · {partner.status}
      </div>
    </button>
  );
}

function PartnerDetail({ partnerId }: { partnerId: number }) {
  const partnerQuery = usePartner(partnerId);
  const keysQuery = usePartnerKeys(partnerId);
  const revokeKey = useRevokePartnerKey(partnerId);
  const updateOrigin = useUpdateKeyOrigin(partnerId);
  const [createKeyOpen, setCreateKeyOpen] = useState(false);

  const partner = partnerQuery.data;
  if (!partner) {
    return (
      <Card pad>
        <div className="muted">Chargement du partenaire…</div>
      </Card>
    );
  }

  const keys = keysQuery.data ?? [];
  const activeCount = keys.filter((k) => k.status === 'ACTIVE').length;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
      <Card pad>
        <div className="fiche-head" style={{ display: 'flex', gap: 18, alignItems: 'flex-start' }}>
          <div
            style={{
              width: 52,
              height: 52,
              borderRadius: 14,
              background: 'var(--accent-tint-2)',
              color: 'var(--accent-ink)',
              display: 'grid',
              placeItems: 'center',
            }}
          >
            <Icon name="plug" size={22} />
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
              <h2 style={{ margin: 0, fontSize: 22, fontWeight: 800, letterSpacing: '-.025em' }}>
                {partner.name}
              </h2>
              <Status kind={partner.status === 'ACTIVE' ? 'ok' : 'warn'}>
                {partner.status}
              </Status>
            </div>
            {partner.description && (
              <div style={{ color: 'var(--ink-2)', fontSize: 13.5, fontWeight: 500, marginTop: 6 }}>
                {partner.description}
              </div>
            )}
            <div
              style={{
                display: 'flex',
                gap: 16,
                flexWrap: 'wrap',
                marginTop: 10,
                fontSize: 12.5,
                fontWeight: 600,
                color: 'var(--ink-3)',
              }}
            >
              {partner.contactName && (
                <span style={{ display: 'inline-flex', gap: 6, alignItems: 'center' }}>
                  <Icon name="user" size={13} />
                  {partner.contactName}
                </span>
              )}
              {partner.contactEmail && (
                <span style={{ display: 'inline-flex', gap: 6, alignItems: 'center' }}>
                  <Icon name="doc" size={13} />
                  {partner.contactEmail}
                </span>
              )}
              {partner.contactPhone && (
                <span style={{ display: 'inline-flex', gap: 6, alignItems: 'center' }}>
                  <Icon name="phone" size={13} />
                  {partner.contactPhone}
                </span>
              )}
            </div>
          </div>
          <div className="fiche-actions" style={{ display: 'flex', gap: 8 }}>
            <Button variant="primary" icon="key" onClick={() => setCreateKeyOpen(true)}>
              Émettre une clé
            </Button>
          </div>
        </div>
      </Card>

      <Card>
        <CardHead
          title="Clés API"
          subtitle={`${activeCount} active${activeCount > 1 ? 's' : ''} sur ${keys.length}`}
        />
        {keys.length === 0 ? (
          <EmptyState
            icon="key"
            title="Aucune clé émise"
            subtitle="Cliquez sur « Émettre une clé » pour générer la première clé. Elle sera affichée en clair une seule fois."
          />
        ) : (
          <KeysGrid
            keys={keys}
            onRevoke={(keyId, reason) => revokeKey.mutate({ keyId, reason })}
            onChangeOrigin={(keyId, source, partnerName) =>
              updateOrigin.mutate({
                keyId,
                recruitmentSource: source,
                recruitmentPartner: partnerName || undefined,
              })
            }
          />
        )}
      </Card>

      <PartnerMetadata partner={partner} />

      <CreateKeyModal
        opened={createKeyOpen}
        partnerId={partner.id}
        onClose={() => setCreateKeyOpen(false)}
      />
    </div>
  );
}

function KeysGrid({
  keys,
  onRevoke,
  onChangeOrigin,
}: {
  keys: ApiKey[];
  onRevoke: (keyId: number, reason: string) => void;
  onChangeOrigin: (
    keyId: number,
    source: KeyRecruitmentSource,
    partnerName: string,
  ) => void;
}) {
  const [editingOrigin, setEditingOrigin] = useState<ApiKey | null>(null);
  const [pendingRevoke, setPendingRevoke] = useState<ApiKey | null>(null);

  const columns = useMemo<ColumnDef<ApiKey>[]>(
    () => [
      {
        accessorKey: 'label',
        header: 'Label',
        size: 220,
        Cell: ({ row }) => (
          <div className="cell-stack">
            <span className="pri">{row.original.label}</span>
            {row.original.revokedReason && (
              <span className="s2">révoquée: {row.original.revokedReason}</span>
            )}
          </div>
        ),
      },
      {
        accessorKey: 'prefix',
        header: 'Préfixe',
        size: 260,
        Cell: ({ cell }) => (
          <span style={{ fontFamily: 'var(--mono)', fontSize: 12 }}>{cell.getValue<string>()}</span>
        ),
      },
      {
        // Sans cette colonne, la configuration des clés n'est pas auditable :
        // une clé partenaire laissée sans origine attribue silencieusement ses
        // dossiers à nos équipes.
        accessorKey: 'recruitmentSource',
        header: 'Origine',
        size: 190,
        Cell: ({ row }) => {
          const src = row.original.recruitmentSource;
          const partner = row.original.recruitmentPartner;
          const label =
            !src || src === 'MAFA_TERRAIN'
              ? 'Nos équipes (MAFA)'
              : (RECRUITMENT_ORIGINS.find((o) => o.value === src)?.label ?? src);
          return (
            <button
              type="button"
              title="Modifier l'origine portée par cette clé"
              onClick={() => setEditingOrigin(row.original)}
              style={{
                all: 'unset',
                cursor: 'pointer',
                textAlign: 'left',
                textDecoration: 'underline dotted',
                textUnderlineOffset: 3,
              }}
            >
              <div className="cell-stack">
                <span className={!src || src === 'MAFA_TERRAIN' ? 's2' : 'pri'}>{label}</span>
                {partner && <span className="s2">{partner}</span>}
              </div>
            </button>
          );
        },
      },
      {
        accessorKey: 'status',
        header: 'Statut',
        size: 130,
        Cell: ({ cell }) => {
          const s = cell.getValue<string>();
          const kind = s === 'ACTIVE' ? 'ok' : s === 'REVOKED' ? 'warn' : 'mute';
          return <Status kind={kind}>{s}</Status>;
        },
      },
      {
        accessorKey: 'useCount',
        header: 'Utilisations',
        size: 120,
        mantineTableHeadCellProps: { align: 'right' },
        mantineTableBodyCellProps: { align: 'right' },
        Cell: ({ cell }) => (
          <span style={{ fontVariantNumeric: 'tabular-nums', fontWeight: 700 }}>
            {fmt(cell.getValue<number>())}
          </span>
        ),
      },
      {
        accessorKey: 'lastUsedAt',
        header: 'Dernière utilisation',
        size: 190,
        Cell: ({ cell }) => (
          <span style={{ fontFamily: 'var(--mono)', fontSize: 12, color: 'var(--ink-3)' }}>
            {formatDateTime(cell.getValue<string>())}
          </span>
        ),
      },
      {
        accessorKey: 'createdAt',
        header: 'Créée le',
        size: 180,
        Cell: ({ cell }) => (
          <span style={{ fontFamily: 'var(--mono)', fontSize: 12, color: 'var(--ink-3)' }}>
            {formatDateTime(cell.getValue<string>())}
          </span>
        ),
      },
      {
        accessorKey: 'expiresAt',
        header: 'Expire',
        size: 180,
        Cell: ({ cell }) => {
          const v = cell.getValue<string | undefined>();
          return (
            <span style={{ fontFamily: 'var(--mono)', fontSize: 12, color: 'var(--ink-3)' }}>
              {v ? formatDateTime(v) : '—'}
            </span>
          );
        },
      },
      {
        accessorKey: 'id',
        header: 'Actions',
        enableSorting: false,
        enableColumnFilter: false,
        size: 140,
        Cell: ({ row }) =>
          row.original.status === 'ACTIVE' ? (
            <Button
              size="sm"
              variant="ghost"
              icon="trash"
              onClick={() => setPendingRevoke(row.original)}
            >
              Révoquer
            </Button>
          ) : (
            <span className="muted" style={{ fontSize: 12 }}>—</span>
          ),
      },
    ],
    [],
  );

  return (
    <>
      <DataGrid
        columns={columns}
        data={keys}
        disableGlobalFilter
        disableColumnFilters
        maxHeight={420}
        emptyMessage="Aucune clé API."
      />
      <EditKeyOriginModal
        apiKey={editingOrigin}
        onClose={() => setEditingOrigin(null)}
        onConfirm={(source, partnerName) => {
          if (editingOrigin) onChangeOrigin(editingOrigin.id, source, partnerName);
          setEditingOrigin(null);
        }}
      />
      <RevokeKeyModal
        apiKey={pendingRevoke}
        onClose={() => setPendingRevoke(null)}
        onConfirm={(reason) => {
          if (pendingRevoke) onRevoke(pendingRevoke.id, reason);
          setPendingRevoke(null);
        }}
      />
    </>
  );
}

function RevokeKeyModal({
  apiKey,
  onClose,
  onConfirm,
}: {
  apiKey: ApiKey | null;
  onClose: () => void;
  onConfirm: (reason: string) => void;
}) {
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);

  function close() {
    setReason('');
    setError(null);
    onClose();
  }

  function submit() {
    setError(null);
    const trimmed = reason.trim();
    if (trimmed.length < 4) {
      setError('La justification doit faire au moins 4 caractères.');
      return;
    }
    onConfirm(trimmed);
    setReason('');
  }

  return (
    <Modal
      opened={apiKey != null}
      onClose={close}
      size={520}
      centered
      withCloseButton={false}
      padding={0}
      overlayProps={{ blur: 2 }}
    >
      {apiKey && (
        <div style={{ display: 'flex', flexDirection: 'column' }}>
          <header
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 12,
              padding: '20px 22px',
              borderBottom: '1px solid var(--line)',
              background: 'var(--panel)',
            }}
          >
            <div
              style={{
                width: 38,
                height: 38,
                borderRadius: 10,
                display: 'grid',
                placeItems: 'center',
                background: 'var(--alert-tint)',
                color: 'var(--alert-ink)',
                border: '1px solid var(--alert-tint-2)',
                flex: '0 0 auto',
              }}
            >
              <Icon name="trash" size={18} />
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontWeight: 800, fontSize: 16.5, letterSpacing: '-.02em', color: 'var(--ink)' }}>
                Révoquer la clé API
              </div>
              <div style={{ fontSize: 12.5, color: 'var(--ink-3)', fontWeight: 600, marginTop: 2 }}>
                Action permanente — la clé ne pourra plus être réactivée
              </div>
            </div>
            <button
              type="button"
              onClick={close}
              aria-label="Fermer"
              style={{
                width: 38,
                height: 38,
                borderRadius: 10,
                border: '1px solid var(--line)',
                background: 'var(--panel)',
                display: 'grid',
                placeItems: 'center',
                cursor: 'pointer',
                color: 'var(--ink-2)',
                boxShadow: 'var(--sh-sm)',
                flex: '0 0 auto',
              }}
            >
              <Icon name="x" size={18} />
            </button>
          </header>

          <div style={{ padding: '22px 24px' }}>
            <div
              style={{
                padding: 14,
                borderRadius: 12,
                background: 'var(--panel-2)',
                border: '1px solid var(--line)',
                marginBottom: 18,
              }}
            >
              <dl className="kv" style={{ gridTemplateColumns: '120px 1fr' }}>
                <dt>Label</dt>
                <dd>{apiKey.label}</dd>
                <dt>Préfixe</dt>
                <dd style={{ fontFamily: 'var(--mono)', fontSize: 12 }}>{apiKey.prefix}</dd>
                <dt>Utilisations</dt>
                <dd>{fmt(apiKey.useCount)}</dd>
              </dl>
            </div>

            <div className="field" style={{ marginBottom: 14 }}>
              <label>
                Justification <span style={{ color: 'var(--alert-ink)' }}>requise</span>
              </label>
              <textarea
                autoFocus
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="ex: clé fuitée le 14/06, nouveau prestataire, fin de contrat…"
                rows={3}
              />
              <div className="hint">
                Sera consignée dans la fiche de la clé et le journal d'audit.
              </div>
            </div>

            {error && <div className="err-msg">{error}</div>}
          </div>

          <footer
            style={{
              display: 'flex',
              gap: 10,
              justifyContent: 'flex-end',
              padding: '16px 22px',
              borderTop: '1px solid var(--line)',
              background: 'var(--panel)',
            }}
          >
            <Button variant="ghost" onClick={close}>
              Annuler
            </Button>
            <Button variant="alert" icon="trash" onClick={submit}>
              Révoquer la clé
            </Button>
          </footer>
        </div>
      )}
    </Modal>
  );
}

function PartnerMetadata({ partner }: { partner: Partner }) {
  const allowed = partner.allowedAttributeTypes ?? [];
  return (
    <Card>
      <CardHead title="Paramètres du partenaire" />
      <div style={{ padding: '6px 22px 14px' }}>
        <dl className="kv">
          <dt>Identifiant</dt>
          <dd style={{ fontFamily: 'var(--mono)', fontSize: 12 }}>{partner.id}</dd>
          <dt>Statut</dt>
          <dd>{partner.status}</dd>
          <dt>Quotas</dt>
          <dd>
            {fmt(partner.rateLimitPerMinute)} req/min · quotidien {partner.dailyQuota ? fmt(partner.dailyQuota) : '—'}
          </dd>
          <dt>Types autorisés</dt>
          <dd>
            {allowed.length > 0 ? (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                {allowed.map((t) => (
                  <span key={t} className="code">{t}</span>
                ))}
              </div>
            ) : (
              'Tous les types autorisés'
            )}
          </dd>
          <dt>Créé le</dt>
          <dd style={{ fontFamily: 'var(--mono)', fontSize: 12 }}>{formatDateTime(partner.createdAt)}</dd>
          <dt>Notes</dt>
          <dd>{partner.notes ?? '—'}</dd>
        </dl>
      </div>
    </Card>
  );
}

function CreatePartnerModal({
  opened,
  onClose,
  onCreated,
}: {
  opened: boolean;
  onClose: () => void;
  onCreated: (p: Partner) => void;
}) {
  const create = useCreatePartner();
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [contactEmail, setContactEmail] = useState('');
  const [contactName, setContactName] = useState('');
  const [error, setError] = useState<string | null>(null);

  function reset() {
    setName('');
    setDescription('');
    setContactEmail('');
    setContactName('');
    setError(null);
  }

  async function submit() {
    setError(null);
    if (!name.trim()) {
      setError('Le nom est requis.');
      return;
    }
    const created = await create.mutateAsync({
      name,
      description: description || undefined,
      contactName: contactName || undefined,
      contactEmail: contactEmail || undefined,
    });
    if (created) {
      reset();
      onCreated(created);
    } else {
      setError('La création a échoué.');
    }
  }

  return (
    <Modal opened={opened} onClose={onClose} title="Nouveau partenaire" size={520} centered>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        <div className="field">
          <label>Nom <span style={{ color: 'var(--alert-ink)' }}>requis</span></label>
          <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Orange Money, PDE.CI, …" />
        </div>
        <div className="field">
          <label>Description</label>
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="À quoi sert ce partenaire ?"
          />
        </div>
        <div className="field">
          <label>Contact (nom)</label>
          <input value={contactName} onChange={(e) => setContactName(e.target.value)} />
        </div>
        <div className="field">
          <label>Contact (email)</label>
          <input value={contactEmail} onChange={(e) => setContactEmail(e.target.value)} />
        </div>
        {error && <div className="err-msg">{error}</div>}
        <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 6 }}>
          <Button variant="ghost" onClick={onClose} disabled={create.isPending}>Annuler</Button>
          <Button variant="primary" icon="check" onClick={submit} loading={create.isPending}>
            Créer le partenaire
          </Button>
        </div>
      </div>
    </Modal>
  );
}

function CreateKeyModal({
  opened,
  partnerId,
  onClose,
}: {
  opened: boolean;
  partnerId: number;
  onClose: () => void;
}) {
  const create = useCreatePartnerKey(partnerId);
  const [label, setLabel] = useState('');
  // Vide = nos équipes. Ce n'est PAS une valeur pré-cochée par commodité : une
  // clé sans origine déclarée est le cas courant (application MAFA), et
  // pré-remplir un partenaire ferait attribuer des dossiers à tort.
  const [recruitmentSource, setRecruitmentSource] = useState<KeyRecruitmentSource>('');
  const [recruitmentPartner, setRecruitmentPartner] = useState('');
  const [issued, setIssued] = useState<IssuedKey | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  function close() {
    setLabel('');
    setIssued(null);
    setError(null);
    setCopied(false);
    onClose();
  }

  async function submit() {
    setError(null);
    if (!label.trim()) {
      setError('Le label est requis.');
      return;
    }
    const key = await create.mutateAsync({
      label,
      recruitmentSource: recruitmentSource || undefined,
      recruitmentPartner: recruitmentPartner.trim() || undefined,
    });
    if (!key) {
      setError('La génération a échoué.');
      return;
    }
    setIssued(key);
  }

  const masked = useMemo(() => {
    if (!issued) return null;
    return `${issued.plaintext.slice(0, 22)}…${issued.plaintext.slice(-6)}`;
  }, [issued]);

  return (
    <Modal opened={opened} onClose={close} title="Émettre une clé API" size={560} centered>
      {!issued ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div className="field">
            <label>Label <span style={{ color: 'var(--alert-ink)' }}>requis</span></label>
            <input
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              placeholder="ex: ingestion-orange-2026"
            />
            <div className="hint">
              Conseil : utilisez un nom qui permet à un opérateur d'identifier l'usage de la clé en un coup d'œil.
            </div>
          </div>

          <div className="field">
            <label>Origine de recrutement</label>
            <select
              value={recruitmentSource}
              onChange={(e) => setRecruitmentSource(e.target.value as KeyRecruitmentSource)}
            >
              <option value="">Nos équipes (MAFA)</option>
              {RECRUITMENT_ORIGINS.filter((o) => o.value !== 'MAFA_TERRAIN').map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
            <div className="hint">
              S'applique aux dossiers <strong>créés</strong> par les événements que cette clé
              authentifie. Une fois posée à la création, l'origine n'est plus modifiée par les
              événements suivants — seule une déclaration faite à l'import d'un fichier de
              transactions peut la remplacer.
            </div>
          </div>

          {recruitmentSource !== '' && (
            <div className="field">
              <label>Partenaire</label>
              <input
                value={recruitmentPartner}
                onChange={(e) => setRecruitmentPartner(e.target.value)}
                placeholder="ex: Orange CI"
              />
              <div className="hint">Facultatif — sert à tracer de quel partenaire vient la clé.</div>
            </div>
          )}
          {error && <div className="err-msg">{error}</div>}
          <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 6 }}>
            <Button variant="ghost" onClick={close} disabled={create.isPending}>Annuler</Button>
            <Button variant="primary" icon="key" onClick={submit} loading={create.isPending}>
              Générer
            </Button>
          </div>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div className="err-msg" style={{ background: 'var(--accent-tint)', borderColor: 'var(--accent-tint-2)', color: 'var(--accent-ink)' }}>
            <Icon name="shieldcheck" /> {issued.oneTimeWarning}
          </div>
          <div>
            <div style={{ fontWeight: 700, fontSize: 12.5, marginBottom: 8 }}>Clé en clair (à copier maintenant) :</div>
            <div className="keybox">
              <code>{copied ? masked : issued.plaintext}</code>
              <Button
                size="sm"
                icon={copied ? 'check' : 'copy'}
                onClick={async () => {
                  try {
                    await navigator.clipboard.writeText(issued.plaintext);
                    setCopied(true);
                  } catch {
                    // Older browsers without async clipboard — leave as is.
                  }
                }}
              >
                {copied ? 'Copié' : 'Copier'}
              </Button>
            </div>
          </div>
          <dl className="kv">
            <dt>Préfixe (stocké en base)</dt>
            <dd style={{ fontFamily: 'var(--mono)', fontSize: 12 }}>{issued.prefix}</dd>
            <dt>Label</dt>
            <dd>{issued.label}</dd>
            <dt>Créée</dt>
            <dd style={{ fontFamily: 'var(--mono)', fontSize: 12 }}>{formatDateTime(issued.createdAt)}</dd>
          </dl>
          <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Button variant="primary" onClick={close} icon="check">
              J'ai stocké la clé
            </Button>
          </div>
        </div>
      )}
    </Modal>
  );
}

/**
 * Modification de l'origine portée par une clé existante.
 *
 * Le rattrapage se fait depuis la colonne où le problème se constate, pas dans
 * un écran séparé : une clé partenaire créée sans origine est repérée en lisant
 * la liste, et se corrige au même endroit.
 */
function EditKeyOriginModal({
  apiKey,
  onClose,
  onConfirm,
}: {
  apiKey: ApiKey | null;
  onClose: () => void;
  onConfirm: (source: KeyRecruitmentSource, partnerName: string) => void;
}) {
  const [source, setSource] = useState<KeyRecruitmentSource>('');
  const [partnerName, setPartnerName] = useState('');
  const [loadedFor, setLoadedFor] = useState<number | null>(null);

  // Recharge les valeurs à chaque ouverture, pour que la modale reflète la clé
  // ouverte et non la précédente.
  if (apiKey && loadedFor !== apiKey.id) {
    setLoadedFor(apiKey.id);
    setSource((apiKey.recruitmentSource ?? '') as KeyRecruitmentSource);
    setPartnerName(apiKey.recruitmentPartner ?? '');
  }

  return (
    <Modal
      opened={apiKey != null}
      onClose={onClose}
      title="Origine de recrutement de la clé"
      size={520}
      centered
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        <div className="field">
          <label>Origine</label>
          <select
            value={source}
            onChange={(e) => setSource(e.target.value as KeyRecruitmentSource)}
          >
            <option value="">Nos équipes (MAFA)</option>
            {RECRUITMENT_ORIGINS.filter((o) => o.value !== 'MAFA_TERRAIN').map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
          <div className="hint">
            Le changement ne vaut que pour l'avenir : les dossiers déjà créés par cette clé
            conservent l'origine posée à leur création.
          </div>
        </div>

        {source !== '' && (
          <div className="field">
            <label>Partenaire</label>
            <input
              value={partnerName}
              onChange={(e) => setPartnerName(e.target.value)}
              placeholder="ex: Orange CI"
            />
          </div>
        )}

        <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 6 }}>
          <Button variant="ghost" onClick={onClose}>
            Annuler
          </Button>
          <Button variant="primary" icon="check" onClick={() => onConfirm(source, partnerName)}>
            Enregistrer
          </Button>
        </div>
      </div>
    </Modal>
  );
}
